import { BooleanInput, coerceBooleanProperty } from "@angular/cdk/coercion";
import { ChangeDetectionStrategy, Component, Input } from "@angular/core";
import { DialogService } from "@dtm-frontend/shared/ui";
import { DmsCoordinatesUtils, GeographicCoordinatesType } from "@dtm-frontend/shared/ui/dms-coordinates";
import { LocalComponentStore } from "@dtm-frontend/shared/utils";
import { map } from "rxjs/operators";
import { DronePointDetails } from "../../../../geo-zones/models/geo-zones.models";
import { DronePointPhotoModalComponent, PhotoModalData } from "../drone-point-photo-modal/drone-point-photo-modal.component";

interface DronePointDetailsComponentState {
    dronePoint: DronePointDetails | undefined;
    isOpened: boolean;
    arePhotoUrlsProcessing: Record<string, boolean>;
}

@Component({
    selector: "dtm-map-drone-point-details",
    templateUrl: "./drone-point-details.component.html",
    styleUrls: ["./drone-point-details.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [LocalComponentStore],
})
export class DronePointDetailsComponent {
    @Input({ required: true }) public set dronePoint(value: DronePointDetails) {
        this.localStore.patchState({
            dronePoint: value,
            arePhotoUrlsProcessing: value.photoUrls.reduce<Record<string, boolean>>(
                (result, photoUrl) => ({ ...result, [photoUrl]: true }),
                {}
            ),
        });
    }
    @Input() public set isOpened(value: BooleanInput) {
        this.localStore.patchState({ isOpened: coerceBooleanProperty(value) });
    }

    protected readonly dronePoint$ = this.localStore.selectByKey("dronePoint");
    protected readonly isOpened$ = this.localStore.selectByKey("isOpened");
    protected readonly arePhotoUrlsProcessing$ = this.localStore
        .selectByKey("arePhotoUrlsProcessing")
        .pipe(map((arePhotoUrlsProcessing) => Object.values(arePhotoUrlsProcessing).every((isProcessing) => isProcessing)));

    constructor(
        private readonly localStore: LocalComponentStore<DronePointDetailsComponentState>,
        private readonly dialogService: DialogService
    ) {
        this.localStore.setState({
            dronePoint: undefined,
            isOpened: false,
            arePhotoUrlsProcessing: {},
        });
    }

    protected displayLatitude(decimal: number) {
        return DmsCoordinatesUtils.convertDmsWithDirectionToString(
            DmsCoordinatesUtils.convertDecimalDegreesToDmsCoordinatesWithDirection(decimal, GeographicCoordinatesType.Latitude)
        );
    }

    protected displayLongitude(decimal: number) {
        return DmsCoordinatesUtils.convertDmsWithDirectionToString(
            DmsCoordinatesUtils.convertDecimalDegreesToDmsCoordinatesWithDirection(decimal, GeographicCoordinatesType.Longitude)
        );
    }

    protected zoomPhoto(index: number) {
        const photoUrls = this.localStore.selectSnapshotByKey("dronePoint")?.photoUrls ?? [];

        if (!photoUrls.length) {
            return;
        }

        this.dialogService.open(DronePointPhotoModalComponent, {
            data: {
                photoUrls,
                currentPhotoIndex: index,
            } satisfies PhotoModalData,
        });
    }

    protected setPhotoUrlProcessing(isProcessing: boolean, photoUrl: string): void {
        this.localStore.patchState(({ arePhotoUrlsProcessing }) => ({
            arePhotoUrlsProcessing: { ...arePhotoUrlsProcessing, [photoUrl]: isProcessing },
        }));
    }
}
